import React, { FC, useEffect, useState } from "react"
import Loading from "../../../../ui/Loading"
import { KTitleSpan, KDropdown, KButton, KSpan, KTooltip, KInput } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { handleAuthorizedRequest, releases, users } from "../../../../../apis"
import { User } from "../../../../../types"
import { toast } from "react-toastify"
import { errorFilledToastOptions } from "../../../../../constants"
import { formatNumber } from "../../../../../utility"

type AdminProfitAndLossProps = {
  setOpenSidebar: (openSidebar: boolean) => void
  openSidebar: boolean
}

const platformOptions: KSelectOption[] = [
  { value: 1, label: "Include DSP", value2: "include_dsp" },
  { value: 2, label: "Platform Only", value2: "platform_only" }
]

const formatOptions: KSelectOption[] = [
  { value: 1, label: "Raw", value2: "raw" },
  { value: 2, label: "Format", value2: "format" }
]

const AdminProfitAndLoss: FC<AdminProfitAndLossProps> = (props) => {
  const [userOptions, setUserOptions] = useState<KSelectOption[]>([])
  const [searchUser, setSearchUser] = useState<boolean>(false)
  const [loading, setLoading] = useState(false)
  const [selectedUser, setSelectedUser] = useState<KSelectOption>()
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>()
  const [startDate, setStartDate] = useState(
    `${new Date(new Date().setFullYear(new Date().getFullYear() - 1)).getFullYear()}${String(
      new Date().getMonth() + 1
    ).padStart(2, "0")}`
  )
  const [endDate, setEndDate] = useState(
    `${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, "0")}`
  )
  const [selectedPlatformOption, setSelectedPlatformOption] = useState(platformOptions[0])
  const [selectedFormatOption, setSelectedFormatOption] = useState(formatOptions[0])

  const onSearchTextChanged = async (text: string) => {
    if (!text) return
    setUserOptions([])
    clearTimeout(timeoutId)
    setTimeoutId(
      setTimeout(async () => {
        await handleAuthorizedRequest(async () => {
          const response = await users(`/?limit=10&offset=0&username=${text}`, "get")
          setUserOptions(
            response.data.results.map((user: User) => ({
              value: user.id,
              label: user.username
            }))
          )
        }, "")
      }, 500)
    )
  }

  const downloadCSV = (data: string[][]) => {
    const csvContent =
      selectedFormatOption.value2 === "raw"
        ? data.map((row) => row.map((item) => `"${item}"`).join(",")).join("\n")
        : data
            .map((row, csvIndex) =>
              row
                .map((item, index) => {
                  // Don't format the first columns: "Date", "Platform", "DSP" (if index < 3)
                  if (
                    (selectedPlatformOption.value2 === "include_dsp" && index < 3) ||
                    (selectedPlatformOption.value2 === "platform_only" && index < 2) ||
                    csvIndex === 0
                  ) {
                    return `"${item}"`
                  }
                  return `"${formatNumber(item, 2) || item}"`
                })
                .join(",")
            )
            .join("\n")

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.setAttribute("download", `finance_report_${startDate}_${endDate}_${selectedPlatformOption.value2}`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const onExportCsv = () => {
    setLoading(true)

    const userParam = selectedUser ? `&user_id=${selectedUser.value}` : ""

    releases(
      `/release/export_finance_reporting/?start_date=${startDate}&end_date=${endDate}&platform_option=${selectedPlatformOption.value2}${userParam}`,
      "get"
    )
      .then((resp) => {
        setLoading(false)
        if (!resp.data.results) {
          toast.error("Error downloading csv", errorFilledToastOptions)
          return
        }
        downloadCSV(resp.data.results)
      })
      .catch((err) => {
        setLoading(false)
        toast.error("Error downloading csv", errorFilledToastOptions)
      })
  }

  const renderDashboard = () => {
    return (
      <div>
        <div className="mt-8">
          <KButton dataTestId="export-csv-button" width="160px" background="black" textColor="white" text="Export CSV" onClick={() => onExportCsv()} />
        </div>
      </div>
    )
  }

  return (
    <div className="catalog flex flex-col bg-[#fff] px-8 pt-4">
      {loading && <Loading />}
      <div className="flex justify-between items-center mb-6">
        <KTooltip
          backgroundColor="#000"
          padding="0px 4px"
          height="20px"
          arrowColor="#000"
          position="bottom"
          content={
            <div className="h-5 w-max flex items-center">
              <KSpan
                text={props.openSidebar ? "Hide sidebar" : "Open sidebar"}
                fontSize={12}
                lineHeight="16px"
                color="#FFF"
              />
            </div>
          }
        >
          <div className="flex items-center">
            <KButton
              dataTestId="reports-sidebar-toggle-button"
              icon="/left-sidebar.svg"
              width="32px"
              height="32px"
              background="transparent"
              shadowDisabled={true}
              padding="6px"
              onClick={() => props.setOpenSidebar(!props.openSidebar)}
            />
            <KTitleSpan fontSize={20} lineHeight="28px" text={"P&L (Profit & Loss Overview)"} />
          </div>
        </KTooltip>
        <div className="flex gap-2">
          <div>
            <KDropdown
              width={110}
              options={formatOptions}
              selected={selectedFormatOption}
              onSelect={(selected) => setSelectedFormatOption(selected as KSelectOption)}
            />
          </div>
          <div>
            <KDropdown
              width={160}
              options={platformOptions}
              selected={selectedPlatformOption}
              onSelect={(selected) => setSelectedPlatformOption(selected as KSelectOption)}
            />
          </div>
          <div>
            <KInput
              value={startDate}
              placeholder={"Start - YYYYMM"}
              onChange={(text) => setStartDate(text)}
              width={120}
            />
          </div>
          <div>
            <KInput value={endDate} placeholder={"End - YYYYMM"} onChange={(text) => setEndDate(text)} width={120} />
          </div>
          <div>
            {searchUser ? (
              <div
                className="relative"
                style={{ width: 250 }}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setSearchUser(false)
                  }
                }}
              >
                <KDropdown
                  placeholder={"Search user to filter"}
                  width={240}
                  isClearable
                  selected={selectedUser}
                  onInputChange={onSearchTextChanged}
                  options={userOptions}
                  onSelect={(selectedOption) => {
                    setSelectedUser(selectedOption as KSelectOption)
                  }}
                />
              </div>
            ) : (
              <div className="">
                <KButton
                  leftIcon="/reports_icons/user.svg"
                  gap="4px"
                  text="Search user"
                  height="36px"
                  width="auto"
                  background="#F7F7F7"
                  hoverBackground="#F3F3F3"
                  activeBackground="#F7F7F7"
                  padding="8px"
                  shadowDisabled
                  onClick={() => {
                    setSearchUser(true)
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {renderDashboard()}
    </div>
  )
}

export default AdminProfitAndLoss
